import { type FC, type Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { type VideoPlayerHandle, VideoPlayer } from '@simplisafe/compass-web'
import { DateTime } from 'luxon'
import { useGetMediatorAccessTokenFn, useGetStreamingUrlFn } from '../../../../api/apiQueries'
import { HttpError } from '../../../../api/HttpError'
import { Banner, BannerType } from '../../../../components/Banner/Banner'
import { LoggerName, namedLogger } from '../../../../utils/logger'
import type { EngagementClip, PlayableSource } from './types'
import { PlayerPlaceholder } from './PlayerPlaceholder'
import { clampSeekOffsetToMedia, createAuthenticatedFlvUrl, getHlsStreamingUrl, getRecordingDetails } from './utils/playbackUtils'
import {
  accessNotAllowed,
  disallowedReasonMessages,
  mediatorErrorMessages,
  mediatorTokenErrorMessages,
  videoClipNotAvailable
} from '../ErtPreprocessor'
import { useParams } from 'react-router-dom'

const logger = namedLogger(LoggerName.InspectPlayerForCamera)

// HTTP failures map like QA mode's ErtPreprocessor handlers; anything else (network failure, a non-JSON
// response body) has no user-meaningful message, so show the map's generic 500 copy and keep the detail
// in the log.
const mapVideoAccessErrorMessage = (e: unknown, messages: Record<number, string>): string =>
  e instanceof HttpError ? (messages[e.status] ?? e.message) : messages[500]

const clipStartMs = (clip: EngagementClip): number => DateTime.fromISO(clip.startTime).toMillis()
const clipEndMs = (clip: EngagementClip): number => DateTime.fromISO(clip.endTime).toMillis()

// Index of the clip whose [start, end] window contains targetAbsoluteTime; if the target falls in a gap
// between clips (or outside all of them), the nearest clip by distance to its window.
function findClipIndexForAbsoluteTime(clips: EngagementClip[], targetAbsoluteTime: number): number {
  for (let i = 0; i < clips.length; i++) {
    if (targetAbsoluteTime >= clipStartMs(clips[i]) && targetAbsoluteTime <= clipEndMs(clips[i])) {
      return i
    }
  }
  let nearestIndex = 0
  let nearestDistance = Number.POSITIVE_INFINITY
  for (let i = 0; i < clips.length; i++) {
    const startMs = clipStartMs(clips[i])
    const endMs = clipEndMs(clips[i])
    const distance = targetAbsoluteTime < startMs ? startMs - targetAbsoluteTime : targetAbsoluteTime - endMs
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestIndex = i
    }
  }
  return nearestIndex
}

const videoPlayerControlsConfig = { volume: false, progress: false, timer: false, playbackRate: false }

// Applied once the swapped-in clip's source is ready (used by both auto-advance and a cross-clip seek).
// Volume carries over on the same <video> element; playbackRate resets to default on load, so we
// re-apply it. seekToSeconds is set only for a seek (auto-advance starts the next clip at 0).
// recordedAtEpoch anchors the bounded wait for the source's duration (see PENDING_SEEK_DURATION_WAIT_MS).
type PendingReadyAction = { seekToSeconds?: number; play: boolean; playbackRate: number; recordedAtEpoch: number }

// How long a pending cross-clip seek waits for the swapped-in source's real duration before clamping
// against the clip's window instead. mpegts.js only reports a duration at end-of-stream, so on a slow or
// stalled FLV download an unbounded wait would leave the seek (and play) permanently unapplied.
const PENDING_SEEK_DURATION_WAIT_MS = 5000

export type InspectPlayerCameraHandle = {
  videoEl: HTMLVideoElement
  // The clip the videoEl's loaded source is ACTUALLY playing. During a source swap (cross-clip seek /
  // auto-advance) this stays on the old clip until the new source is ready, so consumers that map
  // videoEl.currentTime against the clip's start (timeline scrubber, controls' absolute time) never pair
  // the new clip with the old source's position.
  currentClip: EngagementClip
  // True while a cross-clip seek / auto-advance has swapped the source but the new one isn't ready yet -
  // the element's position is transiently meaningless (it can reset to ~0 while the next source attaches).
  isSourceSwapping: boolean
  // Seek to an absolute time (epoch ms - what CameraTimeline emits).
  seek: (absoluteTimeMs: number) => void
}

type InspectPlayerCameraProps = {
  ref?: Ref<InspectPlayerCameraHandle | null>
  aspectRatio: string
  clips: EngagementClip[] // this camera's clips, sorted ascending by startTime
  isSelected: boolean
  // When the engagement ended. A clip straddling it stops playback at the boundary. Null = no bound.
  engagementEndEpoch: number | null
}

export const InspectPlayerForCamera: FC<InspectPlayerCameraProps> = ({ ref, aspectRatio, clips, isSelected, engagementEndEpoch }) => {
  const { sid: locationId, engagementId } = useParams()
  const getMediatorAccessToken = useGetMediatorAccessTokenFn()
  const getStreamingUrl = useGetStreamingUrlFn()

  const [currentClipIndex, setCurrentClipIndex] = useState(0)
  const [playableSource, setPlayableSource] = useState<PlayableSource>()
  const [flvToken, setFlvToken] = useState<string>()
  // Message for a failed async URL resolution (token / streaming URL), mapped like QA mode's
  // ErtPreprocessor handlers. Rendered as a Banner in place of the player.
  const [videoAccessError, setVideoAccessError] = useState<string | null>(null)
  const [videoPlayerHandle, setVideoPlayerHandle] = useState<VideoPlayerHandle | null>(null)
  const pendingReadyActionRef = useRef<PendingReadyAction | null>(null)

  const currentClip = clips[currentClipIndex] ?? clips[0]
  // Index of the clip whose source the videoEl actually has loaded. Trails currentClipIndex across a
  // swap: currentClipIndex flips as soon as a cross-clip seek / auto-advance starts, but the element
  // keeps playing (and reporting currentTime for) the OLD source until the new one is ready, so
  // everything keyed to the element's position (handle consumers, the engagement-end boundary) must use
  // this, not currentClip. Tracked as an index - deriving the clip from `clips` each render picks up the
  // fresh clip objects applyClipMediaDurations mints as FLV probes grow clip durations.
  const [playingClipIndex, setPlayingClipIndex] = useState(currentClipIndex)
  const playingClip = clips[playingClipIndex] ?? clips[0]
  // getRecordingDetails throws when a clip has no playable link (e.g. access disallowed) - surface that
  // as an error banner below instead of crashing the render.
  const recordingDetails = useMemo(() => {
    try {
      return getRecordingDetails(currentClip)
    } catch {
      return null
    }
  }, [currentClip])

  const clipAccessAllowed = currentClip.accessAllowed

  // The user-facing video-access error for the current clip. Disallowed access and a missing playback
  // link are knowable from the clip itself; videoAccessError covers the async resolution failures.
  const errorMessage = !clipAccessAllowed
    ? (currentClip.disallowedReason && disallowedReasonMessages[currentClip.disallowedReason]) || accessNotAllowed
    : recordingDetails
      ? videoAccessError
      : videoClipNotAvailable

  const videoPlayerStyles = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      width: 'auto',
      maxWidth: '100%',
      aspectRatio,
      padding: `var(--spacing-none)`,
      display: isSelected ? undefined : 'none'
    }),
    [isSelected, aspectRatio]
  )

  const videoPlayerRefCb = useCallback((handle: false | VideoPlayerHandle | null) => {
    setVideoPlayerHandle(handle || null)
  }, [])

  const seek = useCallback(
    (absoluteTimeMs: number) => {
      const videoEl = videoPlayerHandle?.videoElement
      if (!videoEl || clips.length === 0) {
        return
      }
      const targetIndex = findClipIndexForAbsoluteTime(clips, absoluteTimeMs)
      const targetClip = clips[targetIndex]
      // Clamp into the target clip's window (handles clicks that land in a gap between clips).
      const clampedAbsoluteTimeMs = Math.min(Math.max(absoluteTimeMs, clipStartMs(targetClip)), clipEndMs(targetClip))
      const offsetSeconds = (clampedAbsoluteTimeMs - clipStartMs(targetClip)) / 1000

      if (targetIndex === currentClipIndex) {
        if (targetIndex === playingClipIndex) {
          // Target clip is the source actually loaded and no swap is in flight - just move the playhead
          // (clamped to the media actually served), leave play state alone.
          videoEl.currentTime = clampSeekOffsetToMedia(offsetSeconds, videoEl.duration)
          return
        }
        // A swap to this clip is already in flight: the element still has the OLD clip loaded, so a direct
        // seek would visibly move the outgoing footage and then be overridden when the swap lands. Retarget
        // the pending action instead - the latest click wins once the new source is ready.
        const pending = pendingReadyActionRef.current
        pendingReadyActionRef.current = {
          seekToSeconds: offsetSeconds,
          play: pending?.play ?? !videoEl.paused,
          playbackRate: videoEl.playbackRate,
          recordedAtEpoch: pending?.recordedAtEpoch ?? Date.now()
        }
        return
      }
      // Seek back into the clip whose source the element STILL has loaded - an in-flight swap whose
      // target never attached a new source (e.g. the target clip errored during resolution). Cancel the
      // swap and seek directly; without this, recovering from an errored clip would wait on a pending
      // action whose source is never going to become ready.
      if (targetIndex === playingClipIndex && playableSource?.recordingId === playingClip.recordingId) {
        pendingReadyActionRef.current = null
        setCurrentClipIndex(targetIndex)
        videoEl.currentTime = clampSeekOffsetToMedia(offsetSeconds, videoEl.duration)
        return
      }
      // Different clip: swap the source, then apply the seek/rate/play once it's ready. Keep play state -
      // if it was playing, resume; if paused (including a not-yet-selected camera), stay paused.
      pendingReadyActionRef.current = {
        seekToSeconds: offsetSeconds,
        play: !videoEl.paused,
        playbackRate: videoEl.playbackRate,
        recordedAtEpoch: Date.now()
      }
      setCurrentClipIndex(targetIndex)
    },
    [videoPlayerHandle, clips, currentClipIndex, playingClipIndex, playingClip, playableSource]
  )

  // The links on a recording are mediator URLs, not directly playable ones - flv streams straight from
  // mediator but needs the access token supplied as the request's Authorization header (passed to
  // compass-web via flvToken), while hls has to be exchanged for a presigned streaming session URL that
  // carries its own auth. We keep the previous URL mounted while resolving the next clip so compass-web
  // swaps the source on the same <video> element (preserving volume and element identity) rather than
  // unmounting and remounting.
  useEffect(() => {
    // A clip change is a fresh resolution attempt - clear the previous clip's error.
    setVideoAccessError(null)
    if (locationId && engagementId && recordingDetails && clipAccessAllowed) {
      const { url: mediatorUrl, provider } = recordingDetails
      let cancelled = false
      const resolvePlayableUrl = async () => {
        let accessToken: string
        try {
          const result = await getMediatorAccessToken({ locationId, engagementId })
          accessToken = result.accessToken
        } catch (e) {
          logger.error('Error getting access token for clip', currentClip, e)
          if (!cancelled) {
            setVideoAccessError(mapVideoAccessErrorMessage(e, mediatorTokenErrorMessages))
            // A failed clip never attaches a source, so a pending seek/play stored for it could only
            // ever be applied to the OLD clip still loaded in the element (the readiness fallback checks
            // nothing but readyState) - drop it. Inside the cancelled guard: if the user has already
            // sought on, the newer clip's resolution run owns the pending action.
            pendingReadyActionRef.current = null
          }
          return
        }

        if (provider === 'flv') {
          if (!cancelled) {
            setFlvToken(accessToken)
            setPlayableSource({ url: createAuthenticatedFlvUrl(mediatorUrl), provider, recordingId: currentClip.recordingId })
          }
          return
        }

        try {
          const streamingUrl = await getHlsStreamingUrl(accessToken, mediatorUrl, getStreamingUrl)
          if (!cancelled) {
            if (streamingUrl) {
              setPlayableSource({ url: streamingUrl, provider, recordingId: currentClip.recordingId })
            } else {
              logger.error('No streaming session URL found in response for clip', currentClip)
              setVideoAccessError(mediatorErrorMessages[404])
              pendingReadyActionRef.current = null
            }
          }
        } catch (e) {
          logger.error('Error getting streaming session URL for clip', currentClip, e)
          if (!cancelled) {
            setVideoAccessError(mapVideoAccessErrorMessage(e, mediatorErrorMessages))
            pendingReadyActionRef.current = null
          }
        }
      }
      resolvePlayableUrl()

      return () => {
        cancelled = true
      }
    } else {
      // Denied clip or no playable link: resolution never even starts, so - like the failure branches
      // above - any pending seek/play stored for this clip must be dropped before the readiness
      // fallback applies it to the old clip's still-loaded source.
      pendingReadyActionRef.current = null
    }
  }, [recordingDetails, clipAccessAllowed, locationId, engagementId, getMediatorAccessToken, getStreamingUrl])

  useImperativeHandle<InspectPlayerCameraHandle | null, InspectPlayerCameraHandle | null>(
    ref,
    () =>
      videoPlayerHandle
        ? {
            videoEl: videoPlayerHandle.videoElement,
            currentClip: playingClip,
            isSourceSwapping: playingClipIndex !== currentClipIndex,
            seek
          }
        : null,
    [videoPlayerHandle, playingClip, playingClipIndex, currentClipIndex, seek]
  )

  // Auto-advance: when the current clip ends, move to the camera's next clip (by startTime order).
  // Changing currentClip swaps videoUrl on the same VideoPlayer; playback resumes once it's ready.
  useEffect(() => {
    if (!videoPlayerHandle) {
      return
    }
    const videoEl = videoPlayerHandle.videoElement
    const handleEnded = () => {
      if (currentClipIndex + 1 >= clips.length) {
        return // no next clip - stop on the last one, like a plain HTML5 video
      }
      pendingReadyActionRef.current = { play: true, playbackRate: videoEl.playbackRate, recordedAtEpoch: Date.now() }
      setCurrentClipIndex(currentClipIndex + 1)
    }
    videoEl.addEventListener('ended', handleEnded)
    return () => videoEl.removeEventListener('ended', handleEnded)
  }, [videoPlayerHandle, clips.length, currentClipIndex])

  // Apply the pending action once the swapped-in clip is ready (see PendingReadyAction).
  useEffect(() => {
    if (!videoPlayerHandle) {
      return
    }
    const videoEl = videoPlayerHandle.videoElement
    let durationWaitTimer: ReturnType<typeof setTimeout> | undefined
    const tryApplyPending = () => {
      const pending = pendingReadyActionRef.current
      if (!pending || videoEl.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
        return
      }
      // A seek prefers the source's real duration for clamping: FLV metadata can land AFTER canplay, and
      // clamping against an unknown duration would let a too-far seek through (the browser then clamps it
      // to the few seconds buffered so far, stranding playback far from the target). But mpegts.js only
      // reports a duration at end-of-stream - the whole file downloaded - so the wait is bounded: past the
      // deadline, fall through and clamp against the clip's window (probe-corrected, so it only spans real
      // footage) rather than leaving the seek and play unapplied on a slow or stalled download.
      if (pending.seekToSeconds !== undefined && !Number.isFinite(videoEl.duration)) {
        const waitedMs = Date.now() - pending.recordedAtEpoch
        if (waitedMs < PENDING_SEEK_DURATION_WAIT_MS) {
          clearTimeout(durationWaitTimer)
          durationWaitTimer = setTimeout(tryApplyPending, PENDING_SEEK_DURATION_WAIT_MS - waitedMs)
          return
        }
      }
      pendingReadyActionRef.current = null
      // The swapped-in source is ready: the element's position now belongs to the target clip.
      setPlayingClipIndex(currentClipIndex)
      videoEl.playbackRate = pending.playbackRate
      if (pending.seekToSeconds !== undefined) {
        const clampDurationSeconds = Number.isFinite(videoEl.duration) ? videoEl.duration : (clipEndMs(currentClip) - clipStartMs(currentClip)) / 1000
        videoEl.currentTime = clampSeekOffsetToMedia(pending.seekToSeconds, clampDurationSeconds)
      }
      if (pending.play && isSelected) {
        videoEl.play().catch(() => {}) // ignore interruption (e.g. a rapid camera switch)
      }
    }
    videoEl.addEventListener('canplay', tryApplyPending)
    videoEl.addEventListener('durationchange', tryApplyPending)
    // Re-arm the deadline for a seek already waiting - this effect re-running (listener re-attach) throws
    // away any previously scheduled check. Events can't be relied on to fire again after a stall.
    const waitingPending = pendingReadyActionRef.current
    if (waitingPending?.seekToSeconds !== undefined) {
      durationWaitTimer = setTimeout(tryApplyPending, Math.max(0, PENDING_SEEK_DURATION_WAIT_MS - (Date.now() - waitingPending.recordedAtEpoch)))
    }
    return () => {
      clearTimeout(durationWaitTimer)
      videoEl.removeEventListener('canplay', tryApplyPending)
      videoEl.removeEventListener('durationchange', tryApplyPending)
    }
    // currentClipIndex keeps the closure current so a ready source is attributed to the right clip
    // (state updates from the seek/advance handlers commit before the new source can fire media events).
    // currentClip keeps the fallback clamp window fresh as FLV probes grow the clip.
  }, [videoPlayerHandle, isSelected, currentClipIndex, currentClip])

  // A deselected camera stays mounted (its stream stays loaded so reselecting is instant) but must not
  // keep playing off-screen.
  useEffect(() => {
    if (!isSelected && videoPlayerHandle && !videoPlayerHandle.videoElement.paused) {
      videoPlayerHandle.videoElement.pause()
    }
  }, [isSelected, videoPlayerHandle])

  // Stop playback at the engagement end: a straddling clip (started before the end, still recording after)
  // must not play past it - the specialist lost access there, so QA review shouldn't see it either.
  // clipStart + currentTime is the clip's absolute time; once it reaches the boundary, clamp and pause. A
  // clip that ends before the boundary never triggers this.
  useEffect(() => {
    if (!videoPlayerHandle || engagementEndEpoch === null) {
      return
    }
    const videoEl = videoPlayerHandle.videoElement
    // playingClip, not currentClip: during a swap the element still reports the OLD source's currentTime,
    // and a boundary computed from the target clip would clamp/pause the old clip spuriously.
    const boundarySeconds = (engagementEndEpoch - clipStartMs(playingClip)) / 1000
    if (boundarySeconds <= 0) {
      return // clip starts at/after the end (shouldn't happen - InspectPlayer filters these out)
    }
    const enforceBoundary = () => {
      if (videoEl.currentTime >= boundarySeconds) {
        if (videoEl.currentTime > boundarySeconds) videoEl.currentTime = boundarySeconds
        if (!videoEl.paused) videoEl.pause()
      }
    }
    videoEl.addEventListener('timeupdate', enforceBoundary)
    return () => videoEl.removeEventListener('timeupdate', enforceBoundary)
  }, [videoPlayerHandle, playingClip, engagementEndEpoch])

  // The error banner renders as a SIBLING of the player, never a replacement: unmounting VideoPlayer
  // fires the ref callback with null, deregistering the camera from InspectPlayer (controls vanish,
  // timeline seeks no-op) with no recovery short of a reload. Kept mounted, the already-loaded footage
  // stays watchable under the banner and a timeline click into a working clip recovers. The banner uses
  // the same display:none approach as the player so a deselected camera never stacks visible content
  // under the selected one's player.
  return (
    <>
      {errorMessage && (
        <Banner
          type={BannerType.Error}
          title={errorMessage}
          data-testid="video-access-error-banner"
          sx={{ marginY: 'var(--spacing-l)', display: isSelected ? undefined : 'none' }}
        />
      )}
      {playableSource && (
        <VideoPlayer
          ref={videoPlayerRefCb}
          videoUrl={playableSource.url}
          videoProviderName={playableSource.provider}
          flvToken={flvToken}
          cameraId={currentClip.cameraId}
          aspectRatio={aspectRatio}
          controls={videoPlayerControlsConfig}
          sx={videoPlayerStyles}
          videoHTMLParams={{ controls: false, autoPlay: false }}
          showStatusBadges={false}
        />
      )}
      {!playableSource && !errorMessage && <PlayerPlaceholder isLoading data-testid="inspect-player-placeholder" aspectRatio={aspectRatio} />}
    </>
  )
}

InspectPlayerForCamera.displayName = 'InspectPlayerForCamera'
